import type { MemberLedgerSummaryDto } from '@pokerroom/types';

export type LedgerTabKind =
  | 'ASSET'
  | 'info'
  | 'PLAYER'
  | 'TIMED'
  | 'Z'
  | 'ZIPA'
  | 'PACKAGES'
  | 'VOUCHERS';

export interface LedgerTab {
  assetTypeId?: string;
  key: string;
  kind: LedgerTabKind;
  /** YYYY-MM, TIMED only */
  period?: string;
}

/**
 * Profile first, then ZIPA (owner 2026-10-19: first balance on the left), then the other ledgers; the Z tab exists only for a Z customer (owner A-189 R3).
 * 堂票紀錄 and 票券紀錄 (Phase 2A) need association mode (the summary carries `packages`) and
 * the `member.points` feature (`wallet`).
 */
export function buildLedgerTabs(
  summary: MemberLedgerSummaryDto | undefined,
  wallet = false,
): LedgerTab[] {
  return [
    { key: 'info', kind: 'info' },
    { key: 'zipa', kind: 'ZIPA' },
    { key: 'player', kind: 'PLAYER' },
    ...(summary?.timed ?? []).map(
      (m): LedgerTab => ({ key: `timed:${m.period}`, kind: 'TIMED', period: m.period }),
    ),
    ...(summary?.z.visible ? [{ key: 'z', kind: 'Z' } as LedgerTab] : []),
    ...(summary?.assets ?? [])
      .filter((asset) => !['PLAYER', 'TIMED', 'Z'].includes(asset.code))
      .flatMap((asset): LedgerTab[] =>
        asset.periodBalances.length
          ? asset.periodBalances.map((row) => ({
              key: `asset:${asset.assetTypeId}:${row.period}`,
              kind: 'ASSET',
              assetTypeId: asset.assetTypeId,
              period: row.period,
            }))
          : [{ key: `asset:${asset.assetTypeId}`, kind: 'ASSET', assetTypeId: asset.assetTypeId }],
      ),
    ...(wallet && summary?.packages
      ? [
          { key: 'packages', kind: 'PACKAGES' } as LedgerTab,
          { key: 'vouchers', kind: 'VOUCHERS' } as LedgerTab,
        ]
      : []),
  ];
}

/** The requested tab if it exists, else the profile tab. */
export function resolveTab(requested: string | null, tabs: LedgerTab[]): LedgerTab {
  return tabs.find((tab) => tab.key === requested) ?? tabs[0]!;
}

export const monthNumber = (period: string) => Number(period.slice(5));

/** Translation key and values for a 限時 tab; the year is added only when two tabs share a month number. */
export function timedLabel(period: string, allPeriods: string[]) {
  const month = monthNumber(period);
  const repeated = allPeriods.filter((other) => monthNumber(other) === month).length > 1;
  return repeated
    ? { key: 'ledgerTabTimedYear', values: { year: period.slice(0, 4), month } }
    : { key: 'ledgerTabTimed', values: { month } };
}

/** Balances arrive as decimal strings (Z点 may be fractional). */
export function formatPoints(value: string): string {
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
}
